import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {compoundConditionOutputSchema} from '../../promptToCardAgentV2.schemas';
import {CompoundConditionCardScratchpadState} from '../../scratchpad/compoundConditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import validateCompoundConditionOutput from '../../utils/compoundConditions/validateCompoundConditionOutput';
import setRuleSignal from '../../utils/compoundConditions/setRuleSignal';
import setRuleOperator from '../../utils/compoundConditions/setRuleOperator';
import setRuleValue from '../../utils/compoundConditions/setRuleValue';
import {
  CompoundConditionNodeOutputData,
  CompoundConditionNodeRule,
  CompoundConditionNodeSection,
} from 'common/src/modules/decisionTree/decisionTree.types';
import _findLastIndex from 'lodash/findLastIndex';

const addCompoundConditionOutputToolSchema = z.object({
  output: compoundConditionOutputSchema,
});

export default function addCompoundConditionOutputTool() {
  async function execute(
    params: ToolExecuteParams<
      typeof addCompoundConditionOutputToolSchema,
      CompoundConditionCardScratchpadState
    >,
  ) {
    const {output} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<CompoundConditionCardScratchpadState>(
      agentScratchpad,
      NodeType.COMPOUND_CONDITION,
    );

    const validationError = validateCompoundConditionOutput(output, validatedScratchpad.card);

    if (validationError) {
      return toolError(validationError);
    }

    const sections: CompoundConditionNodeSection[] = [];

    for (const section of output.sections) {
      const rules: CompoundConditionNodeRule[] = [];

      for (const rule of section.rules) {
        const signalResult = setRuleSignal(
          rule.signal.value,
          rule.signal.name,
          rule.signal.categories ?? [],
          validatedScratchpad.card,
        );

        if (typeof signalResult === 'string') {
          return toolError(signalResult);
        }

        const operatorResult = setRuleOperator(rule.ruleOperator);

        if (typeof operatorResult === 'string') {
          return toolError(operatorResult);
        }

        const valueResult = setRuleValue(rule.ruleValue, rule.ruleOperator);

        if (typeof valueResult === 'string') {
          return toolError(valueResult);
        }

        rules.push({
          signal: signalResult,
          ruleOperator: operatorResult,
          ruleValue: valueResult,
        });
      }

      sections.push({rules});
    }

    const newOutput: CompoundConditionNodeOutputData = {
      id: validatedScratchpad.card.outputs.length + 1,
      nodeId: NO_NODE_ID,
      name: output.name,
      type: OutputType.Regular,
      sections,
    };

    const lastRegularOutputIndex = _findLastIndex(
      validatedScratchpad.card.outputs,
      (o) => o.type === OutputType.Regular,
    );

    if (lastRegularOutputIndex === -1) {
      validatedScratchpad.card.outputs.unshift(newOutput);
    } else {
      validatedScratchpad.card.outputs.splice(lastRegularOutputIndex + 1, 0, newOutput);
    }

    return toolResult('Compound condition output added');
  }

  return tool<typeof addCompoundConditionOutputToolSchema, CompoundConditionCardScratchpadState>({
    name: V2CardAgentToolName.AddCompoundConditionOutput,
    displayName: 'Adding compound condition output',
    description:
      'Add a new output to a compound condition card. Each output contains sections (AND logic) with rules (OR logic). Each rule specifies a signal, operator, and value.',
    inputSchema: addCompoundConditionOutputToolSchema,
    execute,
  });
}
